import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Label} from "../drawer/drawer/Style";
import SearchSelectorPanel from "./SearchSelectorPanel";
import EntityTypeIcon from "./EntityTypeIcon";
import {useClickOutside} from "../../hooks/useClickOutside";
import {
    applySearchDrawerDropdownSizing,
    computeDropdownFixedPosition,
    getDropdownPortalStyle,
} from "../../utils/selectbox/dropdownViewportPosition";
import {useInSearchDetail} from "../search/SearchDetailContext";
import {getUser} from "../../api/user/UserAccountService";
import {buildMultiArrayLabel} from "./searchSelectorUtils";
import "./searchSelector.css";

const MODAL_MIN_WIDTH = 260;
const ESTIMATED_MODAL_HEIGHT = 400;

function getItemLabel(item) {
    return item?.name ?? item?.assetName ?? item?.title ?? "";
}

function SearchSelector({
    onChange,
    data,
    title,
    entity,
    parentKey,
    constraint,
    auth,
    recursive,
    placeHolder,
    uniqueKey,
    children,
    showLabel,
    notInsert,
    useMultiSearch,
    multiSelect,
    variant,
    readOnly,
    /** `document.body` 포털 z-index. JsExcelGrid fullmode(9999) 위에 띄울 때 10050 등 지정. */
    dropdownZIndex,
    /** id가 없을 때 name/label로 active 매칭(엑셀 등 특수 케이스) */
    matchActiveByNameWhenIdMissing = false,
    /** 드롭다운 내 "선택 취소" 버튼 표시(엑셀 등) */
    showClearAction = false,
}) {
    const inSearchDetail = useInSearchDetail();
    const enableMultiSelect = (multiSelect ?? useMultiSearch) && !inSearchDetail;
    const isCardVariant = variant === "card";
    const isControlledMulti = enableMultiSelect && Array.isArray(data);
    const [label, setLabel] = useState();
    const [resolvedLabel, setResolvedLabel] = useState(null);
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const modalRef = useRef(null);
    const [modalPos, setModalPos] = useState({top: 0, left: 0});

    const drawerMultiSearchKey = `drawerMultiSearch:${title}:${entity}:${parentKey ?? ""}:${uniqueKey ?? ""}`;

    const multiArrayLabel = useMemo(
        () => buildMultiArrayLabel(data, placeHolder),
        [data, placeHolder],
    );

    const defaultLabel = isControlledMulti
        ? multiArrayLabel
        : resolvedLabel ?? data?.name ?? data?.assetName ?? data?.title ?? placeHolder ?? (isCardVariant ? "선택 없음" : "선택");
    const hasSelection = isControlledMulti
        ? data.length > 0
        : !!(data && (data.id != null || data.name || data.assetName || data.title));

    useEffect(() => {
        let cancelled = false;

        if (!Array.isArray(data) && data?.rawValue === "USER" && data?.id != null) {
            getUser(data.id)
                .then((response) => {
                    if (!cancelled) {
                        setResolvedLabel(response?.data?.name ?? null);
                    }
                })
                .catch(() => {
                    if (!cancelled) setResolvedLabel(null);
                });
            return () => {
                cancelled = true;
            };
        }

        setResolvedLabel(null);
        return undefined;
    }, [data?.id, data?.rawValue, data?.name, data?.assetName, data?.title]);
    const isRequiredEmpty = constraint?.required && (label === "선택" || !hasSelection);

    const selectedItems = useMemo(() => {
        if (isControlledMulti) {
            return data;
        }
        if (enableMultiSelect && label && label !== defaultLabel && label !== "선택") {
            return [{name: label}];
        }
        if (hasSelection && !Array.isArray(data)) {
            return [data];
        }
        return [];
    }, [isControlledMulti, enableMultiSelect, label, defaultLabel, hasSelection, data]);

    const displayLabel = isControlledMulti ? multiArrayLabel : (label ?? defaultLabel);

    const summary = useMemo(() => {
        const emptyText = placeHolder ?? (isCardVariant ? "선택 없음" : "선택");
        if (isControlledMulti) {
            return multiArrayLabel;
        }
        if (enableMultiSelect && label && label !== defaultLabel && label !== "선택") {
            return label;
        }
        if (hasSelection && displayLabel) {
            return displayLabel;
        }
        return emptyText;
    }, [isControlledMulti, multiArrayLabel, label, defaultLabel, displayLabel, hasSelection, enableMultiSelect, placeHolder, isCardVariant]);

    const avatars = selectedItems.slice(0, 3);
    const isValueSelected = hasSelection || selectedItems.length > 0;
    const closePanel = useCallback(() => setOpen(false), []);

    useClickOutside([modalRef, triggerRef], closePanel, open);

    useEffect(() => {
        if (!enableMultiSelect || isControlledMulti) {
            if (!isControlledMulti) {
                setLabel(defaultLabel);
            }
            return;
        }

        const saved = sessionStorage.getItem(drawerMultiSearchKey);
        if (!saved) {
            setLabel(defaultLabel);
            return;
        }

        try {
            const parsed = JSON.parse(saved);
            const checkedNodeIds = Array.isArray(parsed?.checkedNodeIds) ? parsed.checkedNodeIds : [];
            if (parsed?.enabled && checkedNodeIds.length > 0 && typeof parsed?.displayLabel === "string" && parsed.displayLabel.trim() !== "") {
                setLabel(parsed.displayLabel);
                return;
            }
        } catch (e) {
            // ignore
        }

        setLabel(defaultLabel);
    }, [data, placeHolder, enableMultiSelect, drawerMultiSearchKey, defaultLabel, isControlledMulti]);

    useEffect(() => {
        if (!enableMultiSelect || isControlledMulti) return;
        const saved = sessionStorage.getItem(drawerMultiSearchKey);
        if (!saved && label !== defaultLabel) {
            setLabel(defaultLabel);
        }
    });

    useEffect(() => {
        const handleDrawerReset = () => {
            sessionStorage.removeItem(drawerMultiSearchKey);
            if (enableMultiSelect) {
                onChange({__multiFlat: []});
            }
            setLabel(defaultLabel);
        };

        window.addEventListener("simms:reset-drawers", handleDrawerReset);
        return () => window.removeEventListener("simms:reset-drawers", handleDrawerReset);
    }, [drawerMultiSearchKey, enableMultiSelect, onChange, defaultLabel]);

    useLayoutEffect(() => {
        if (!open) return;

        const updatePosition = () => {
            const triggerEl = triggerRef.current;
            if (!triggerEl) return;
            const pos = computeDropdownFixedPosition({
                triggerEl,
                dropdownEl: modalRef.current,
                dropdownMinWidth: MODAL_MIN_WIDTH,
                estimatedHeight: ESTIMATED_MODAL_HEIGHT,
                contentSized: true,
            });
            applySearchDrawerDropdownSizing(modalRef.current, triggerEl, pos, {widePanel: false});
            setModalPos(pos);
        };

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);

        const ro = new ResizeObserver(updatePosition);
        if (modalRef.current) ro.observe(modalRef.current);

        const onWin = () => updatePosition();
        window.addEventListener("resize", onWin);
        window.addEventListener("scroll", onWin, true);
        window.visualViewport?.addEventListener("resize", onWin);
        window.visualViewport?.addEventListener("scroll", onWin);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin, true);
            window.visualViewport?.removeEventListener("resize", onWin);
            window.visualViewport?.removeEventListener("scroll", onWin);
        };
    }, [open, data, children]);

    const toggle = (e) => {
        e.stopPropagation();
        if (readOnly) return;
        if (!auth) {
            alert("권한이 없습니다.");
            return;
        }
        setOpen((prev) => !prev);
    };

    const triggerClassName = [
        "search-selector-trigger",
        "simms-drawer-trigger",
        isCardVariant ? "search-selector-trigger--card" : "search-selector-trigger--compact",
        isValueSelected ? "is-selected" : "",
        isRequiredEmpty ? "is-required-empty" : "",
        open ? "is-open" : "",
    ].filter(Boolean).join(" ");

    const renderCompactTrigger = () => (
        <>
            <span className="search-selector-summary">{summary}</span>
            <svg
                className={`search-selector-chevron ${open ? "is-open" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
            >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        </>
    );

    const renderCardTrigger = () => (
        <div className="search-selector-card-body">
            <span className="search-selector-icon" aria-hidden="true">
                <EntityTypeIcon entity={entity}/>
            </span>

            {enableMultiSelect && avatars.length > 0 && (
                <span className="search-selector-avatars">
                    {avatars.map((item, idx) => (
                        <span
                            key={`${item?.id ?? "x"}-${idx}`}
                            className="search-selector-avatar"
                        >
                            {(getItemLabel(item) || "?").slice(0, 1)}
                        </span>
                    ))}
                    {selectedItems.length > 3 && (
                        <span className="search-selector-avatar">+{selectedItems.length - 3}</span>
                    )}
                </span>
            )}

            <span className="search-selector-card-text">
                {title && (
                    <span className="search-selector-card-label">{title}</span>
                )}
                <span className="search-selector-card-summary">{summary}</span>
            </span>

            <svg
                className={`search-selector-chevron ${open ? "is-open" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
            >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className={`simms-drawer-field search-selector-field ${showLabel ? "search-selector-field--inline" : ""} ${isCardVariant ? "search-selector-field--card" : ""}`}>
            {showLabel && !isCardVariant && <Label>{title}</Label>}

            <div
                ref={triggerRef}
                className={triggerClassName}
                onClick={toggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        toggle(e);
                    }
                }}
            >
                {isCardVariant ? renderCardTrigger() : renderCompactTrigger()}
            </div>

            {open && typeof document !== "undefined" && createPortal(
                <div
                    ref={modalRef}
                    className={`fixed ${dropdownZIndex == null ? "z-[1100]" : ""} search-selector-dropdown ${modalPos.inSearchDrawer ? "search-detail-drawer-dropdown" : ""}`}
                    style={{
                        ...getDropdownPortalStyle(modalPos, {
                            extendedPanel: false,
                            defaultWidth: MODAL_MIN_WIDTH,
                            extendedWidth: MODAL_MIN_WIDTH,
                        }),
                        ...(dropdownZIndex != null ? {zIndex: dropdownZIndex} : {}),
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => {
                        /** JsExcelGrid: 편집 셀 바깥 mousedown 으로 편집기가 닫히며 선택 onChange 가 씹히는 것 방지 */
                        if (dropdownZIndex != null) e.stopPropagation();
                    }}
                >
                    <SearchSelectorPanel
                        open={open}
                        onClose={closePanel}
                        useMultiSearch={enableMultiSelect}
                        controlledMulti={isControlledMulti}
                        data={data}
                        onChange={onChange}
                        recursive={recursive}
                        label={label}
                        constraint={constraint}
                        placeHolder={placeHolder}
                        setLabel={setLabel}
                        children={children}
                        title={title}
                        entity={entity}
                        uniqueKey={uniqueKey}
                        parentKey={parentKey}
                        showLabel={showLabel}
                        notInsert={notInsert}
                        matchActiveByNameWhenIdMissing={matchActiveByNameWhenIdMissing}
                        showClearAction={showClearAction}
                    />
                </div>,
                document.body,
            )}
        </div>
    );
}

export default SearchSelector;
